import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller } from 'react-hook-form';
import useEventsDrawerFilters, {
  EventsDrawerApplyPayload,
  EventsDrawerFilterDraft,
} from './events-drawer-filters.hook';

type EventsDrawerFiltersProps = {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (payload: EventsDrawerApplyPayload) => void;
  appliedFilters: EventsDrawerFilterDraft | null;
};

export default function EventsDrawerFilters({
  isOpen,
  onClose,
  onApplyFilters,
  appliedFilters,
}: EventsDrawerFiltersProps) {
  const hook = useEventsDrawerFilters({ isOpen, onClose, onApplyFilters, appliedFilters });
  const filterCardSx = {
    borderRadius: 1,
    boxShadow: 'none',
    borderColor: 'grey.200',
  };

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      anchor="right"
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 560, md: 620 },
          maxWidth: '100vw',
          backgroundColor: 'grey.50',
        },
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Filtros de Experiências
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Refine a listagem com os critérios desejados
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
          <Stack spacing={2}>
            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Período
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Controller
                      name="startDate"
                      control={hook.control}
                      render={({ field }) => (
                        <DatePicker
                          label="Data de início"
                          value={field.value}
                          onChange={(value) => hook.handleChangeDate('startDate', value)}
                          slotProps={{ textField: { fullWidth: true, variant: 'filled' } }}
                        />
                      )}
                    />
                    <Controller
                      name="endDate"
                      control={hook.control}
                      render={({ field }) => (
                        <DatePicker
                          label="Data de fim"
                          value={field.value}
                          minDate={hook.draftFilters.startDate || undefined}
                          onChange={(value) => hook.handleChangeDate('endDate', value)}
                          slotProps={{ textField: { fullWidth: true, variant: 'filled' } }}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={filterCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Status
                  </Typography>
                  <Autocomplete
                    options={hook.statusOptions}
                    value={
                      hook.statusOptions.find(
                        (option) => option.value === hook.draftFilters.status
                      ) || null
                    }
                    onChange={(_, selected) => hook.handleChangeStatus(selected?.value || '')}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField {...params} variant="filled" label="Status" />
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>

        <Divider />
        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ p: 2.5 }}>
          <Button variant="outlined" onClick={hook.handleClear}>
            Limpar
          </Button>
          <Button variant="contained" onClick={hook.handleApply}>
            Aplicar filtros
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
